import MainLayout from "@/components/layout/admin/MainLayout.vue";
import axios from "axios";
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import CONFIG from "@/assets/js/config.js";
import ACCESS_CONTROL from "@/utils/access-control";

export default {
  name: "EmployeeProfile",
  components: {
    MainLayout,
    Loading
  },

  data() {
    const store = this.$store;
    return {
      isLoading: false,
      teacherId: null,
      employeeData: {},
      noDataMsg: CONFIG.NO_DATA_EXIST,
      CONFIG,
      religionArr: null,
      defaultUrl: process.env.VUE_APP_STORAGE_PATH,
      academicYear: [],
      hasSubject: false,
      subjectArr: [],
      attendanceInfo: [],
      courseInfo: {},
      teacherTypeArr: [],
      adminNo: store?.state?.user?.admin_no,
      userRole: store?.state?.user?.role,
      currentUserEmployeeId: store?.state?.user?.employee?.id ?? store?.state?.user?.employee_id
    };
  },

  computed: {
    isLoggedInTeacher() {
      return this.auth('teacher').check();
    },

    isOwnProfile() {
      const requestedEmployeeId = this.$route.params.id;
      return String(this.currentUserEmployeeId) === String(requestedEmployeeId);
    },

    canViewEmployeeList() {
      const user = this.$store.state.user;
      if (!user || !this.auth('admin').check()) return false;

      const isSuperAdmin = user.admin_no === "0001" || Number(user.role) === CONFIG.ROLE.ID.SUPER_ADMIN;
      const isHrOperator = Number(user.role) === CONFIG.ROLE.ID.HR_OPERATOR;

      return isSuperAdmin || isHrOperator;
    },

    homeRoute() {
      return this.isLoggedInTeacher ? 'teacherDashboard' : 'dashboard';
    },

    isTeacher() {
      return Number(this.employeeData.employeeable_flag) === CONFIG.EMPLOYEE_FLAG.TEACHER;
    },

    teacherTypeName() {
      if (!this.employeeData?.teacher_type_id || !this.teacherTypeArr) {
        return '';
      }

      let list = [];
      if (Array.isArray(this.teacherTypeArr)) {
        list = this.teacherTypeArr;
      } else if (Array.isArray(this.teacherTypeArr.data)) {
        list = this.teacherTypeArr.data;
      } else if (typeof this.teacherTypeArr === 'object') {
        list = Object.values(this.teacherTypeArr);
      }

      const targetId = String(this.employeeData.teacher_type_id);
      const match = list.find(item => item && String(item.id) === targetId);

      return match ? match.type_name : '';
    },

    canEditEmployee() {
      const user = this.$store.state.user;
      if (!user || !this.auth('admin').check()) return false;
      if (user.admin_no === "0001" || Number(user.role) === CONFIG.ROLE.ID.SUPER_ADMIN) return true;
      const permissions = ACCESS_CONTROL.EMPLOYEE?.create ?? [];
      return permissions.includes(Number(user.role));
    },
  },

  methods: {
    getThisAcademicYear() {
      axios.get('period/getThisPeriod')
        .then(res => {
          this.academicYear = res.data;
          this.getSubjectByTeacherId();
          this.getCourseInfo();
          this.getAttendanceInfo();
        }).catch((error) => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
        });
    },

    getSubjectByTeacherId() {
      axios.get("subject/getSubjectByTeacherId/" + this.employeeData.teacher_id, {
        params: {
          acd_year_id: this.academicYear.id,
          campus_id: this.$store.state.user.campus_id
        }
      })
        .then(response => {
          this.subjectArr = response.data;
        })
        .catch(error => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
          console.log("Subject API Error", error);
        });
    },

    getReligion() {
      const result = [{
        id: 0,
        text: 'Select'
      }];
      Object.entries(CONFIG.RELIGION).forEach(([id, key]) => {
        result.push({
          id: id,
          text: key
        });
      });
      this.religionArr = result;
    },

    getEmployeeDetail() {
      this.isLoading = true;

      const requestedEmployeeId = this.$route.params.id;
      const isSuperAdmin = this.adminNo === '0001' || Number(this.userRole) === CONFIG.ROLE.ID.SUPER_ADMIN;
      const isHrOperator = Number(this.userRole) === CONFIG.ROLE.ID.HR_OPERATOR;

      if (!isSuperAdmin && !isHrOperator && !this.isOwnProfile) {
        this.employeeData = {};
        this.isLoading = false;
        return;
      }

      axios.get("/employee/" + requestedEmployeeId, {
        params: {
          campus_id: this.$store.state.user.campus_id
        }
      })
        .then((response) => {
          this.employeeData = response.data;
          if (this.employeeData.employeeInfo != null) {
            this.employeeData.employeeInfo.url = this.defaultUrl + this.employeeData.employeeInfo.url;
          }
          if (this.isTeacher) {
            this.getThisAcademicYear();
          }
          this.isLoading = false;
        })
        .catch((error) => {
          if (error.response.status === 403) {
            this.employeeData = {};
            this.isLoading = false;
            return;
          } else if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
          console.log("Employee Detail API Error", error);
        });
    },

    getCourseInfo() {
      axios.get("/teacher/course/" + this.employeeData.teacher_id, {
        params: {
          acd_year_id: this.academicYear.id,
          campus_id: this.$store.state.user.campus_id
        }
      })
        .then(response => {
          if (response.data.length > 0) {
            this.hasSubject = true;
            this.courseInfo = response.data;
          }
        })
        .catch(error => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
          console.log("Course API Error", error);
        });
    },

    formatDateRange(startDate, endDate) {
      if (!startDate) return '';
      const start = new Date(startDate).getFullYear();
      if (endDate) {
        const end = new Date(endDate).getFullYear();
        return `${start} - ${end}`;
      }
      return `${start} - Present`;
    },

    confirmDelete(id) {
      if (window.confirm("Are you sure you want to delete?")) {
        this.deleteEmployee(id);
      }
    },
    getAttendanceInfo() {
      var request = {
        params: {
          start_date: this.academicYear.start_date,
          employee_id: this.$route.params.id,
          campus_id: this.$store.state.user.campus_id
        }
      }
      axios.get("/employee-attendance/info", request)
        .then(response => {
          this.attendanceInfo = response.data;
        })
        .catch(error => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
          console.log("Attendance API Error", error);
        });
    },

    deleteEmployee(id) {
      axios.delete("/employee/" + id)
        .then(response => {
          if (response.data.success == true) {
            this.$router.push({ name: "employeeList" });
            this.$notification.success('Employee Deleted Successfully', {
              timer: 3,
              position: "bottomCenter"
            });
          }
        })
        .catch(error => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          console.log("Employee Delete Error : ", error);
        });
    },

    downloadPdfForQrCode() {
      axios.get("/teacher/downloadPdf/" + this.$route.params.id, {
        params: {
          campus_id: this.$store.state.user.campus_id,
          t_name: this.employeeData.name,
          t_father_name: this.employeeData.father_name,
          employee_no: this.employeeData.employee_no,
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob'
      })
        .then(response => {
          if (response.status == 200) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            var currentDate = new Date();
            const fileName = `qrcode_${this.employeeData.name}_${currentDate.getFullYear()}${(currentDate.getMonth() + 1) > 9 ? '' : '0'}${currentDate.getMonth() + 1}${(currentDate.getDate()) > 9 ? '' : '0'}${currentDate.getDate()}.pdf`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
          }
        })
        .catch(error => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
        });
    },

    getTeacherType() {
      axios.get('getTeacherType')
        .then(res => {
          this.teacherTypeArr = res.data.data || res.data || [];
        }).catch((error) => {
          if (error.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
        });
    },
  },

  mounted() {
    this.isLoading = true;
    this.getReligion();
    this.getEmployeeDetail();
    this.getTeacherType();
  },
};
